import type { Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Badge,
  Box,
  IconNewWindow,
  Inline,
  Stack,
  Text,
} from 'braid-design-system';

import { analyticsEvents } from 'src/constants/analytics';
import { urlResolver } from 'src/utils/config';

import TrackingButtonLink from '../TrackingButtonLink/TrackingButtonLink';

import translations from './.vocab';
import SmartHireLogo from './png/smarthire-logo.png';
import SrnLogo from './png/srn-logo.png';
import { useFindRecruiterDialogTracking } from './useFindRecruiterDialogTracking';

import * as styles from './FindRecruiterDialog.css';

interface Props {
  locale: Locale;
  onClose: () => void;
}

const FindRecruiterContent = ({ locale, onClose }: Props) => {
  const { t } = useTranslations(translations);
  const {
    alertShownMessage,
    alertShownMessageCode,
    alertShownTitle,
    alertShownTone,
    alertShownType,
    actionOrigin,
  } = useFindRecruiterDialogTracking();

  let logo = null,
    description,
    buttonText,
    href,
    logoClass;
  switch (locale) {
    case 'en-MY':
    case 'en-HK':
      logo = SmartHireLogo;
      description = t('SmartHire Description');
      buttonText = t('SmartHire Button Text');
      href = '/products/smarthire?ref=hirer-dashboard';
      logoClass = styles.smarthireLogo;
      break;
    default:
      logo = SrnLogo;
      description = t('SRN Description');
      buttonText = t('SRN Button Text');
      href = '/recruiter-network/?ref=hirer-dashboard';
      logoClass = styles.srnLogo;
      break;
  }

  return (
    // Padding in designs are "xlarge" for desktop and "medium" for mobile
    <Box
      paddingY={{ mobile: 'medium', desktop: 'xlarge' }}
      paddingX="medium"
      height="full"
      display="flex"
      alignItems="center"
    >
      <Stack space="medium">
        <Inline space="xsmall">
          <Box
            component="img"
            className={logoClass}
            src={logo}
            alt="SRN Logo"
          />
          <Badge tone="positive">{t('New')}</Badge>
        </Inline>
        <Stack space="medium">
          <Text size="large" weight="strong">
            {t('Dialog Heading')}
          </Text>
          <Text>{description}</Text>
        </Stack>
        <TrackingButtonLink
          trackingEventName={analyticsEvents.TRACK_CONTENT_ELEMENT}
          trackingActionOrigin={actionOrigin}
          trackingData={{
            elementAction: 'internal redirect',
            elementDesign: 'button',
            elementText: buttonText,
            alertShown: {
              type: alertShownType,
              tone: alertShownTone,
              title: alertShownTitle,
              message: alertShownMessage,
              messageCode: alertShownMessageCode,
            },
          }}
          href={urlResolver({ path: href })}
          icon={<IconNewWindow />}
          iconPosition="trailing"
          tone="brandAccent"
          size="small"
          onClick={onClose}
          target="_blank"
          rel="noopener noreferrer"
        >
          {buttonText}
        </TrackingButtonLink>
      </Stack>
    </Box>
  );
};

export default FindRecruiterContent;
